/**
 * useSowStudio - the SOW Template Studio's editing state machine.
 *
 * Owns the document-first editing state (settings, logical sections, preview
 * sample, visual overrides) on top of the same engine the old workbench used
 * (`@protoss/doc` + `lib/sowContract`), and adds what the studio needs that the
 * workbench did not: always-on editing, debounced DRAFT AUTOSAVE, dirty
 * tracking against the last saved snapshot, optimistic-concurrency (stale-write)
 * handling, and the publish lifecycle (with the blocking conformance gate).
 *
 * Visual overrides are applied into the live draft so the preview reflects a
 * pending cover/logo change; on a successful save they are folded into `base`
 * (no remount, no re-application) and cleared.
 */

import type { DocumentContract } from '@protoss/doc';
import {
  applyVisualOverrides,
  countLiteralAccentColors,
  findDominantAccentColor,
  normalizeAccentColorsToBrandToken,
  type VisualOverrides,
} from '@protoss/doc';
import * as React from 'react';
import { type PreviewMode, type PreviewSample } from '../../../../components/doc';
import {
  addSection,
  buildSowContract,
  defaultPreviewSample,
  duplicateSection,
  moveSection,
  parseDocumentSettings,
  parseLogicalSections,
  removeSection,
  renameSection,
  reorderSection,
  switchSectionType,
  type LogicalContentType,
  type LogicalSection,
  type SowDocumentSettings,
} from '../../lib/sowContract';
import {
  cloneSowTemplate,
  publishSowTemplate,
  saveSowTemplate,
  type PublishSowTemplateResult,
  type SowStudioTemplate,
} from '../../lib/sowTemplateApi';
import { readPageChrome, writePageChrome, type PageChrome } from '../../lib/sowPageChrome';
import type { SowWorkspaceBrand } from '../sow-template/SowCoverBrandPanel';

/** Debounce before a dirty draft autosaves (ms). */
const AUTOSAVE_DELAY_MS = 1200;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const FALLBACK_CONTRACT: DocumentContract = {
  provider: 'docx',
  schemaVersion: '0',
  output: { filename: '' },
  document: {},
  sections: [],
} as unknown as DocumentContract;

function asContract(value: unknown): DocumentContract | null {
  if (value && typeof value === 'object' && Array.isArray((value as DocumentContract).sections)) {
    return value as DocumentContract;
  }
  return null;
}

function mergeSample(base: PreviewSample, persisted?: PreviewSample | null): PreviewSample {
  if (!persisted) return base;
  return {
    version: '1.0',
    tokens: { ...base.tokens, ...persisted.tokens },
    data: { ...base.data, ...persisted.data },
    narrative: { ...base.narrative, ...persisted.narrative },
  };
}

export function hasVisualOverrides(v: VisualOverrides): boolean {
  return (
    (v.assets?.length ?? 0) > 0 ||
    Boolean(v.addCoverAssetUuid) ||
    Boolean(v.addLogoAssetUuid) ||
    Boolean(v.addLogoWorkspace)
  );
}

function snapshot(contract: DocumentContract, sample: PreviewSample, slots: Record<string, string>): string {
  return JSON.stringify([contract, sample, slots]);
}

export interface SowStudioState {
  readonly canEdit: boolean;
  readonly contractReadable: boolean;
  readonly settings: SowDocumentSettings;
  readonly sections: LogicalSection[];
  readonly sample: PreviewSample;
  readonly assetSlots: Record<string, string>;
  readonly visualOverrides: VisualOverrides;
  readonly selectedId: string | null;
  readonly previewMode: PreviewMode;
  /** The live contract (logical sections + settings + any pending visual overrides applied). */
  readonly draftContract: DocumentContract;
  readonly resolvedAssetSlots: Record<string, string>;
  readonly headings: Array<{ text: string; level: number }>;
  readonly pageChrome: PageChrome;
  /** Off-brand literal heading colors that could be normalized to the brand token. */
  readonly literalAccentColorCount: number;
  /** The dominant off-brand heading color (hex, no #) - the candidate to promote to the brand. */
  readonly dominantOffBrandColor: string | null;
  readonly isDirty: boolean;
  readonly version: string;
  // lifecycle status
  readonly saveStatus: SaveStatus;
  readonly saveError: string | null;
  readonly stale: { currentVersion?: string } | null;
  readonly publishing: boolean;

  // section/document intents
  setSettings: (s: SowDocumentSettings) => void;
  setSample: (s: PreviewSample) => void;
  setSelectedId: (id: string | null) => void;
  setPreviewMode: (m: PreviewMode) => void;
  setVisualOverrides: (v: VisualOverrides) => void;
  setPageChrome: (patch: Partial<PageChrome>) => void;
  normalizeBrandColors: () => void;
  rename: (id: string, text: string) => void;
  switchType: (id: string, type: LogicalContentType) => void;
  addSectionOfType: (type: LogicalContentType) => void;
  changeSection: (next: LogicalSection) => void;
  move: (id: string, dir: 'up' | 'down') => void;
  reorder: (id: string, toIndex: number) => void;
  duplicate: (id: string) => void;
  remove: (id: string) => void;

  // lifecycle
  saveNow: () => Promise<void>;
  overwriteStale: () => Promise<void>;
  publish: (force?: boolean) => Promise<PublishSowTemplateResult>;
  duplicateTemplate: () => Promise<string | null>;
}

export function useSowStudio(args: {
  productUuid: string;
  template: SowStudioTemplate;
  workspaceBrand: SowWorkspaceBrand;
}): SowStudioState {
  const { productUuid, template, workspaceBrand } = args;
  const canEdit = template.scope === 'workspace';
  const contractReadable = asContract(template.contract) != null;
  const initialBase = asContract(template.contract) ?? FALLBACK_CONTRACT;
  const workspaceLogoUuid =
    workspaceBrand.documentLogoAssetUuid ?? workspaceBrand.headerLogoAssetUuid ?? null;

  const [base, setBase] = React.useState<DocumentContract>(initialBase);
  const [settings, setSettings] = React.useState<SowDocumentSettings>(() =>
    parseDocumentSettings(initialBase)
  );
  const [sections, setSections] = React.useState<LogicalSection[]>(() =>
    parseLogicalSections(initialBase)
  );
  const [sample, setSample] = React.useState<PreviewSample>(() =>
    mergeSample(defaultPreviewSample(initialBase), template.metadata?.previewSample)
  );
  const [assetSlots, setAssetSlots] = React.useState<Record<string, string>>(
    () => template.metadata?.assetSlots ?? {}
  );
  const [visualOverrides, setVisualOverrides] = React.useState<VisualOverrides>({});
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [previewMode, setPreviewMode] = React.useState<PreviewMode>('sample');

  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('idle');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [stale, setStale] = React.useState<{ currentVersion?: string } | null>(null);
  const [publishing, setPublishing] = React.useState(false);

  const draft = React.useMemo(() => {
    const built = buildSowContract(base, settings, sections);
    if (!hasVisualOverrides(visualOverrides)) return { contract: built, assetSlots };
    return applyVisualOverrides(built, assetSlots, visualOverrides, workspaceLogoUuid);
  }, [base, settings, sections, assetSlots, visualOverrides, workspaceLogoUuid]);

  const headings = React.useMemo(
    () =>
      sections
        .filter((s) => s.heading)
        .map((s) => ({ text: s.headingText, level: s.headingLevel ?? 1 })),
    [sections]
  );

  // The saved baseline: the freshly-built initial contract so a just-loaded
  // template reads as not-dirty (matches the workbench's normalized baseline).
  const versionRef = React.useRef(template.version);
  const savedSnapshotRef = React.useRef<string>('');
  const savedSlotsRef = React.useRef<Record<string, string>>(template.metadata?.assetSlots ?? {});
  if (savedSnapshotRef.current === '') {
    savedSnapshotRef.current = snapshot(
      buildSowContract(initialBase, parseDocumentSettings(initialBase), parseLogicalSections(initialBase)),
      mergeSample(defaultPreviewSample(initialBase), template.metadata?.previewSample),
      template.metadata?.assetSlots ?? {}
    );
  }

  const currentSnapshot = snapshot(draft.contract, sample, draft.assetSlots);
  const isDirty = currentSnapshot !== savedSnapshotRef.current;

  // Latest-value refs so the debounced autosave never closes over stale state.
  const latest = React.useRef({ draft, sample, visualOverrides, snapshot: currentSnapshot });
  latest.current = { draft, sample, visualOverrides, snapshot: currentSnapshot };
  const savingRef = React.useRef(false);

  const performSave = React.useCallback(
    async (opts: { overwrite?: boolean }) => {
      if (!canEdit || savingRef.current) return;
      const { draft: d, sample: s, visualOverrides: vo, snapshot: snap } = latest.current;
      if (snap === savedSnapshotRef.current) {
        setSaveStatus('saved');
        return;
      }
      savingRef.current = true;
      setSaveStatus('saving');
      setSaveError(null);
      const slotsChanged = JSON.stringify(d.assetSlots) !== JSON.stringify(savedSlotsRef.current);
      const res = await saveSowTemplate(productUuid, template.uuid, {
        contract: d.contract,
        previewSample: s,
        ...(slotsChanged ? { assetSlots: d.assetSlots } : {}),
        ...(opts.overwrite ? {} : { expectedVersion: versionRef.current }),
      });
      savingRef.current = false;
      if (res.ok) {
        versionRef.current = res.version;
        savedSnapshotRef.current = snap;
        savedSlotsRef.current = d.assetSlots;
        // Fold consumed visual overrides into `base` so they persist without
        // re-application; sections/settings are unchanged so nothing remounts.
        if (hasVisualOverrides(vo)) {
          setBase(d.contract);
          setAssetSlots(d.assetSlots);
          setVisualOverrides({});
        }
        setStale(null);
        setSaveStatus('saved');
      } else if (res.code === 'STALE_WRITE') {
        setStale({ currentVersion: res.currentVersion });
        setSaveStatus('error');
        setSaveError(res.message);
      } else {
        setSaveStatus('error');
        setSaveError(res.message);
      }
    },
    [canEdit, productUuid, template.uuid]
  );

  // Debounced draft autosave: re-armed on every edit; suppressed while a
  // stale-write is unresolved (the user must reload or explicitly overwrite).
  React.useEffect(() => {
    if (!canEdit || !isDirty || stale) return;
    const t = window.setTimeout(() => void performSave({}), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [canEdit, isDirty, stale, currentSnapshot, performSave]);

  // ── intents ──
  const rename = React.useCallback((id: string, text: string) => {
    setSections((prev) => {
      const r = renameSection(prev, latest.current.sample, id, text);
      setSample(r.sample);
      return r.sections;
    });
  }, []);
  const switchType = React.useCallback((id: string, type: LogicalContentType) => {
    setSections((prev) => {
      const r = switchSectionType(prev, latest.current.sample, id, type);
      setSample(r.sample);
      return r.sections;
    });
  }, []);
  const addSectionOfType = React.useCallback(
    (type: LogicalContentType) => {
      setSections((prev) => {
        const before = new Set(prev.map((s) => s.id));
        const r = addSection(prev, latest.current.sample, type, selectedId ?? undefined);
        setSample(r.sample);
        const created = r.sections.find((s) => !before.has(s.id));
        if (created) setSelectedId(created.id);
        return r.sections;
      });
    },
    [selectedId]
  );
  const changeSection = React.useCallback((next: LogicalSection) => {
    setSections((prev) => prev.map((s) => (s.id === next.id ? next : s)));
  }, []);
  const move = React.useCallback((id: string, dir: 'up' | 'down') => {
    setSections((prev) => moveSection(prev, id, dir));
  }, []);
  const reorder = React.useCallback((id: string, toIndex: number) => {
    setSections((prev) => reorderSection(prev, id, toIndex));
  }, []);
  const duplicate = React.useCallback((id: string) => {
    setSections((prev) => {
      const r = duplicateSection(prev, latest.current.sample, id);
      setSample(r.sample);
      return r.sections;
    });
  }, []);
  const remove = React.useCallback(
    (id: string) => {
      setSections((prev) => removeSection(prev, id));
      if (selectedId === id) setSelectedId(null);
    },
    [selectedId]
  );

  const setPageChrome = React.useCallback(
    (patch: Partial<PageChrome>) => {
      setBase((prev) => writePageChrome(prev, settings.bodyPageSectionIndex, patch));
    },
    [settings.bodyPageSectionIndex]
  );

  const normalizeBrandColors = React.useCallback(() => {
    const { contract, normalized } = normalizeAccentColorsToBrandToken(latest.current.draft.contract);
    if (normalized > 0) {
      setBase(contract);
      setSettings(parseDocumentSettings(contract));
      setSections(parseLogicalSections(contract));
      setVisualOverrides({});
    }
  }, []);

  const saveNow = React.useCallback(() => performSave({}), [performSave]);
  const overwriteStale = React.useCallback(() => performSave({ overwrite: true }), [performSave]);

  const publish = React.useCallback(
    async (force?: boolean) => {
      setPublishing(true);
      try {
        // Flush any pending edits first so we publish the latest draft.
        if (latest.current.snapshot !== savedSnapshotRef.current) await performSave({});
        return await publishSowTemplate(productUuid, template.uuid, { force: force ?? false });
      } finally {
        setPublishing(false);
      }
    },
    [performSave, productUuid, template.uuid]
  );

  const duplicateTemplate = React.useCallback(async () => {
    try {
      const { uuid } = await cloneSowTemplate(productUuid, template.uuid, `${template.label} (copy)`);
      return uuid;
    } catch {
      return null;
    }
  }, [productUuid, template.uuid, template.label]);

  return {
    canEdit,
    contractReadable,
    settings,
    sections,
    sample,
    assetSlots,
    visualOverrides,
    selectedId,
    previewMode,
    draftContract: draft.contract,
    resolvedAssetSlots: draft.assetSlots,
    headings,
    pageChrome: readPageChrome(draft.contract, settings.bodyPageSectionIndex),
    literalAccentColorCount: countLiteralAccentColors(draft.contract),
    dominantOffBrandColor: findDominantAccentColor(draft.contract),
    isDirty,
    version: versionRef.current,
    saveStatus,
    saveError,
    stale,
    publishing,
    setSettings,
    setSample,
    setSelectedId,
    setPreviewMode,
    setVisualOverrides,
    setPageChrome,
    normalizeBrandColors,
    rename,
    switchType,
    addSectionOfType,
    changeSection,
    move,
    reorder,
    duplicate,
    remove,
    saveNow,
    overwriteStale,
    publish,
    duplicateTemplate,
  };
}
